/**
 * One crypto deposit as a ticket, opened from the deposit history: its status and progress, what the
 * client should do next, the amounts and IDs, and (while it waits for coins) the address and Cancel.
 * It follows the payment live, like the payment screen.
 */
import { useState, type ReactNode } from 'react';
import { cryptoCoin, fmt, type CryptoPayment } from '@platform/shared';
import { cx } from '../../../components/ui';
import { navigate } from '../../../lib/router';
import { Chip, CopyButton, PillButton, PillLink, QrCode, when } from '../kit';
import { CoinIcon, coinLabel } from './coins';
import { CancelPayment, mmss, OPEN, STATUS, useFollowPayment, useNow } from './payment';
import { Sheet } from './Sheet';
import { TicketFrame } from './TicketFrame';

const STEPS = ['Waiting for payment', 'Confirming', 'Credited'];

export function DepositTicket({ p: initial, onClose, onChanged }: { p: CryptoPayment; onClose: () => void; onChanged?: () => void }) {
  const [p, setP] = useState<CryptoPayment | null>(initial);
  useFollowPayment(p, setP);
  const cur = p ?? initial;
  return (
    <Sheet title="Deposit ticket" onClose={onClose}>
      <Ticket
        p={cur}
        onClose={onClose}
        onCancelled={(x) => {
          setP(x);
          onChanged?.();
        }}
      />
    </Sheet>
  );
}

function Ticket({ p, onClose, onCancelled }: { p: CryptoPayment; onClose: () => void; onCancelled: (p: CryptoPayment) => void }) {
  const k = cryptoCoin(p.payCurrency);
  const now = useNow(p.status === 'waiting');
  const left = p.expiresAt ? Math.max(0, Date.parse(p.expiresAt) - now) : null;
  const expired = p.status === 'expired' || (p.status === 'waiting' && left === 0);
  const status = expired ? 'expired' : p.status;
  const [label, tone] = STATUS[status];
  const payable = p.status === 'waiting' && !expired;
  const paid = p.actuallyPaid && Number(p.actuallyPaid) > 0 ? p.actuallyPaid : null;
  const ref = p.paymentId ?? p.id;

  // Progress: done steps green, the current one blue; a payment that stopped marks where it stopped.
  const at = status === 'waiting' ? 0 : OPEN.includes(status) ? 1 : status === 'finished' ? 3 : status === 'partially_paid' ? 1 : 0;
  const stopped = !OPEN.includes(status) && status !== 'finished';

  const msg: [string, ReactNode] =
    status === 'waiting'
      ? ['bg-warn-bg', <>Send exactly <b className="num">{p.payAmount} {k.symbol}</b>{k.network ? <> on the <b>{k.network}</b> network</> : null}{left !== null ? <> within <b className="num">{mmss(left)}</b></> : null}. Other coins or networks are lost.</>]
      : OPEN.includes(status)
        ? ['bg-track', 'We have seen your payment on the network. It is credited automatically once confirmed, usually within 30 minutes. Nothing to do.']
        : status === 'finished'
          ? ['bg-up-bg', <b key="f">${fmt(p.creditedUsd ?? p.amountUsd, 2)} was added to your live account.</b>]
          : status === 'partially_paid'
            ? ['bg-warn-bg', <><b>Part of your payment arrived.</b> We received {paid ?? 'less than'} {k.symbol} of the {p.payAmount} {k.symbol} due. Our team will credit what arrived in USDT and contact you.</>]
            : status === 'cancelled'
              ? ['bg-track', 'You cancelled this payment. Do not send coins to its address.']
              : status === 'refunded'
                ? ['bg-track', 'This payment was refunded.']
                : status === 'failed'
                  ? ['bg-down-bg', 'This payment failed. Nothing was taken from your wallet by us; start a new deposit.']
                  : ['bg-track', 'This address expired before a payment arrived. If you already sent coins, get help with the transaction hash. Otherwise start a new deposit for a fresh rate.'];

  const rows: [string, ReactNode][] = [
    ['Deposit', `$${fmt(p.amountUsd, 2)}`],
    [payable ? 'Amount to send' : 'Amount due', p.payAmount ? `${p.payAmount} ${k.symbol}` : '–'],
    ['Network', k.network || k.symbol],
  ];
  if (paid) rows.push(['Received', `${paid} ${k.symbol}`]);
  if (p.creditedUsd) rows.push(['Credited', `$${fmt(p.creditedUsd, 2)}`]);
  if (p.payExtraId) rows.push(['Memo / tag', p.payExtraId]);
  rows.push(['Created', when(p.createdAt, true)], ['Last update', when(p.updatedAt, true)]);
  if (payable && p.expiresAt) rows.push(['Pay before', when(p.expiresAt)]);
  rows.push(['Deposit ID', p.id]);
  if (p.paymentId) rows.push(['NOWPayments ID', p.paymentId]);

  return (
    <TicketFrame
      head={
        <>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-4">
            <CoinIcon code={p.payCurrency} size={44} />
            <div className="min-w-0">
              <div className="num text-[28px] leading-tight font-semibold md:text-[30px]">
                {status === 'finished' ? '+' : ''}${fmt(p.creditedUsd ?? p.amountUsd, 2)}
              </div>
              <div className="truncate text-[14px] text-muted">{coinLabel(p.payCurrency)}</div>
            </div>
          </div>
          <Chip tone={tone}>{label}</Chip>
        </div>
        <ol className="m-0 grid list-none grid-cols-3 gap-1.5 p-0" aria-label="Progress">
          {STEPS.map((t, i) => {
            const state = i < at ? 'done' : i === at && !stopped ? 'now' : i === at && stopped ? 'stop' : 'todo';
            return (
              <li key={t} aria-current={state === 'now' ? 'step' : undefined} className={cx('flex flex-col gap-1.5 text-[12px]', state === 'todo' ? 'text-muted' : 'text-fg')}>
                <span className={cx('h-1 rounded-full', { done: 'bg-up', now: 'bg-accent', stop: 'bg-down', todo: 'bg-pill' }[state])} />
                {t}
              </li>
            );
          })}
        </ol>
        </>
      }
    >
        <div className={cx('rounded-[14px] px-4 py-3 text-[14px] leading-relaxed md:text-[15px]', msg[0])}>{msg[1]}</div>

        {payable && p.payAddress && (
          <div className="flex flex-wrap items-center gap-4 rounded-[16px] border border-line p-4">
            <QrCode value={p.payAddress} size={112} label={`${coinLabel(p.payCurrency)} payment address QR code`} />
            <div className="flex min-w-[200px] flex-1 flex-col gap-2.5">
              <span className="text-[14px] font-semibold">{coinLabel(p.payCurrency)} address</span>
              <code className="num rounded-[10px] bg-track px-3 py-2.5 text-[13px] break-all">{p.payAddress}</code>
              <div className="flex flex-wrap gap-2">
                <CopyButton value={p.payAddress} size="sm">
                  Copy address
                </CopyButton>
                <CopyButton value={p.payAmount ?? ''} size="sm" kind="secondary">
                  Copy amount
                </CopyButton>
                {p.payExtraId && (
                  <CopyButton value={p.payExtraId} size="sm" kind="secondary">
                    Copy memo
                  </CopyButton>
                )}
              </div>
            </div>
          </div>
        )}

        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[14px]">
          {rows.map(([t, v]) => (
            <div key={t} className="contents">
              <dt className="text-muted">{t}</dt>
              <dd className="num m-0 text-right break-all">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap gap-2">
          {payable && <CancelPayment p={p} onDone={onCancelled} />}
          {!payable && (
            <CopyButton value={p.id} size="sm" kind="secondary">
              Copy deposit ID
            </CopyButton>
          )}
          {(expired || ['cancelled', 'failed', 'refunded'].includes(status)) && (
            <PillButton
              size="sm"
              onClick={() => {
                onClose();
                navigate('/account/deposit?method=crypto');
              }}
            >
              Start a new deposit
            </PillButton>
          )}
          <PillLink to={`/account/support?topic=funding&ref=${encodeURIComponent(ref)}`} size="sm" kind="secondary">
            Get help with this deposit
          </PillLink>
        </div>
    </TicketFrame>
  );
}
